import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ToastService } from '@shared/services';
import { DebugInfoService } from '@shared/services/debug-info/debug-info.service';

type DebugInfoDialogState = 'unavailable' | 'collecting' | 'done' | 'failed';

@Component({
  selector: 'app-debug-info-dialog',
  templateUrl: './debug-info-dialog.component.html',
  styleUrls: ['./debug-info-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DebugInfoDialogComponent implements OnInit {
  public state: DebugInfoDialogState = 'collecting';
  public agentLogAvailable = true;
  public errorMessage: string;

  private abortController = new AbortController();

  constructor(
    public dialogRef: MatDialogRef<DebugInfoDialogComponent>,
    private debugInfoService: DebugInfoService,
    private toast: ToastService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    if (!this.debugInfoService.canGenerate()) {
      this.state = 'unavailable';
      return;
    }

    void this.collect();
  }

  public onCancel(): void {
    this.abortController.abort();
    this.dialogRef.close();
  }

  public onClose(): void {
    this.dialogRef.close();
  }

  private async collect(): Promise<void> {
    try {
      const result = await this.debugInfoService.generate(this.abortController.signal);
      this.agentLogAvailable = result.agentLogAvailable;
      this.state = 'done';
      this.toast.short('Debug info downloaded and summary copied to clipboard.', null, 'success');
    } catch (error) {
      if (this.abortController.signal.aborted) {
        return;
      }
      this.state = 'failed';
      this.errorMessage = error instanceof Error ? error.message : String(error);
    }

    this.changeDetectorRef.markForCheck();
  }
}
